/**
 * Maps timecards onto the published shifts they cover, so a schedule card can
 * show the times that were actually worked in place of the ones that were
 * planned.
 */

// Coverage and no-show rules are shared with the reports and attendance
// surfaces, per frontend/AGENTS.md, so a fix in one reaches all of them.
import {
  filterCoveredNoShowRows,
  getCoveredPublishedShifts,
  getPublishedShiftHours,
} from "./attendanceCoverage";

/**
 * Quarter-hour time value, or NaN when there isn't one. Number() alone turns
 * null and "" into midnight, which would render an absent punch as a real
 * 12:00a - 12:00a shift instead of falling back to the scheduled times.
 */
function toHours(value) {
  if (value === null || value === undefined || value === "") return NaN;
  return Number(value);
}

/**
 * Build { [publishedScheduleId]: { shift_start, shift_end, in_progress } }
 * from a get_actual_used_schedule payload.
 *
 * Two rows are deliberately left out:
 *
 *   - No-show placeholders, which the backend appends for published shifts
 *     that never produced a timecard. They carry no worked time.
 *   - Rows covering more than one published shift. That worked span belongs to
 *     no single card, and writing it onto each one would show the same hours
 *     twice over.
 *
 * Callers should fall back to the shift's scheduled times when a published id
 * is absent from the result.
 */
export function buildActualTimesByPublishedId(rows) {
  // Collected first, merged after. A shift can be punched in several segments,
  // and the time between them is off the clock: merging as we go would stretch
  // 9-12 and 1-5 into a continuous 9-5 and lose the hour in the middle, so the
  // card would draw eight hours while the totals counted seven.
  const segmentsById = {};

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.result_start === "no show") return;

    const covered = getCoveredPublishedShifts(row);
    if (covered.length !== 1) return;

    const publishedId = covered[0]?.id;
    if (publishedId == null) return;

    const start = toHours(row?.shift_start);
    const end = toHours(row?.shift_end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;

    if (!segmentsById[publishedId]) segmentsById[publishedId] = [];
    segmentsById[publishedId].push({
      start,
      end,
      in_progress: row?.result_end === "in_progress",
      unpaid_breaks: Array.isArray(row?.unpaid_breaks)
        ? row.unpaid_breaks.filter(Boolean)
        : [],
      paid_breaks: Array.isArray(row?.paid_breaks)
        ? row.paid_breaks.filter(Boolean)
        : [],
      store_team_member_id: row?.store_team_member?.id ?? null,
      // Kept for payloads that report a break as a total with no entries.
      total_break: toHours(row?.total_break),
      break_start: toHours(row?.break_start),
    });
  });

  const byId = {};
  Object.keys(segmentsById).forEach((publishedId) => {
    const segments = segmentsById[publishedId]
      .slice()
      .sort((a, b) => a.start - b.start);

    // A shift split between the scheduled employee and a coworker produces
    // segments under one published id owned by different people. Merging those
    // into a single span would draw both their hours on whichever card the
    // first segment happened to belong to, so the span is withheld and the card
    // falls back to saying the shift was worked.
    const owners = new Set(
      segments
        .map((segment) => segment.store_team_member_id)
        .filter((id) => id != null)
    );
    if (owners.size > 1) return;

    const unpaidBreaks = [];
    segments.forEach((segment, index) => {
      if (segment.unpaid_breaks.length > 0) {
        unpaidBreaks.push(...segment.unpaid_breaks);
      } else {
        // Asked per segment rather than of the merged list, because a gap
        // synthesized below would otherwise count as an entry and suppress it,
        // leaving hours short of what the card draws.
        const total = totalOnlyBreakEntry(
          segment.start,
          segment.end,
          segment.total_break,
          segment.break_start
        );
        if (total) unpaidBreaks.push(total);
      }
      // The gap to the next segment is time not worked, so it reads as a break
      // and the drawn span matches the hours counted.
      const next = segments[index + 1];
      if (next && next.start > segment.end) {
        unpaidBreaks.push({
          break_start: segment.end,
          break_length: next.start - segment.end,
          is_off_clock_gap: true,
        });
      }
    });

    byId[publishedId] = {
      shift_start: segments[0].start,
      shift_end: Math.max(...segments.map((segment) => segment.end)),
      in_progress: segments.some((segment) => segment.in_progress),
      unpaid_breaks: unpaidBreaks,
      paid_breaks: segments.flatMap((segment) => segment.paid_breaks),
      store_team_member_id:
        segments.find((segment) => segment.store_team_member_id != null)
          ?.store_team_member_id ?? null,
    };
  });

  return byId;
}

/**
 * Whether a scheduled shift produced no timecard at all.
 *
 * This asks the shift, not buildActualTimesByPublishedId's output. A shift can
 * be missing from that map and still have been worked: the map deliberately
 * skips punches covering several published shifts, and a shift someone else
 * covered was worked by somebody. has_actual_used_schedule is computed
 * server-side with coverage taken into account, so it is the honest test.
 *
 * Returns false for an unpublished shift, which has no timecard to be missing.
 */
export function hasNoTimecard(shift) {
  return (
    shift?.publishedschedule?.has_actual_used_schedule === false &&
    // A member who isn't expected to clock in has no timecard to be missing.
    !shift?.publishedschedule?.clock_in_optional
  );
}

/**
 * Every published shift id that some timecard accounts for.
 *
 * Unlike buildActualTimesByPublishedId this keeps multi-shift punches, because
 * the question here is only "was this worked", not "which times belong on this
 * card". Callers use it to override a cached has_actual_used_schedule flag,
 * which rides on actual_schedule and goes stale when only the punches are
 * refreshed.
 */
export function buildCoveredPublishedIds(rows) {
  const ids = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.result_start === "no show") return;
    getCoveredPublishedShifts(row).forEach((published) => {
      if (published?.id != null) ids.add(published.id);
    });
  });
  return ids;
}

/**
 * Hours actually worked on a timecard row.
 *
 * total_break is the authority: the endpoint computes it as the consolidated
 * span minus time actually worked, so it captures the off-clock gaps between
 * punch segments as well as the breaks somebody explicitly recorded. Summing
 * only the explicit breaks misses those gaps: two segments of 9-12 and 1-5 with
 * a half-hour break come to 6.5 worked hours, not 7.5.
 *
 * The explicit breaks are still a floor, so a row that somehow arrives without
 * a consolidated total does not report a whole shift as worked.
 */
function num(value) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

export function workedHoursFromPunch(row) {
  const explicitBreaks = Array.isArray(row?.unpaid_breaks)
    ? row.unpaid_breaks.reduce(
        (sum, entry) => sum + num(entry?.break_length ?? entry?.break_duration),
        0
      )
    : 0;
  const breakTime = Math.max(num(row?.total_break), explicitBreaks);
  return Math.max(0, num(row?.shift_end) - num(row?.shift_start) - breakTime);
}

/**
 * Who worked each published shift, by published id.
 *
 * A timecard can be linked to a shift assigned to somebody else, which is what
 * covering a coworker looks like in the data. Keying punches by published id
 * alone would draw the coverer's clock times on the scheduled employee's card,
 * so callers compare this against the card's own employee before showing times.
 *
 * Unlike the times map this keeps multi-shift punches, since the question here
 * is only who worked the shift.
 */
export function buildCoverageOwners(rows) {
  const owners = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.result_start === "no show") return;
    const ownerId = row?.store_team_member?.id ?? null;
    getCoveredPublishedShifts(row).forEach((published) => {
      if (published?.id == null) return;
      if (owners[published.id] == null) owners[published.id] = ownerId;
    });
  });
  return owners;
}

/**
 * Some POS payloads report a break as a total with no entries. Drawn from that
 * total, the break lands where the payload says it started, or centred in the
 * span when it does not say. Without this the timeline paints a break as
 * worked time while the hours beside it subtract the break.
 */
function totalOnlyBreakEntry(start, end, totalBreak, breakStart) {
  if (!Number.isFinite(totalBreak) || totalBreak <= 0) return null;
  const from = Number.isFinite(breakStart)
    ? breakStart
    : (start + end) / 2 - totalBreak / 2;
  return { break_start: from, break_length: totalBreak };
}

/**
 * The role a punch belongs to when it did not clock one.
 *
 * A punch covering shifts from two departments belongs wholly to neither, so
 * it stays unscoped rather than handing the whole span to whichever shift
 * sorted first. Agreement is asked at the department level, because that is
 * what the filter scopes by: two Service roles are one department, and
 * comparing role ids would strand that punch outside a Service filter its own
 * work belongs in. Role ids are only compared when no department is known.
 */
function fallbackRoleFromCoverage(coveredShifts) {
  const unscoped = { role_id: null, role_type: null };
  const entries = coveredShifts
    .map((shift) => ({
      id: shift?.role_id?.id ?? shift?.role_id ?? null,
      type: shift?.role_type ?? null,
    }))
    .filter((entry) => entry.id != null || entry.type != null);
  if (entries.length === 0) return unscoped;

  const types = new Set(
    entries.map((entry) => entry.type).filter((type) => type != null)
  );
  if (types.size > 1) return unscoped;

  if (types.size === 0) {
    const ids = new Set(entries.map((entry) => entry.id));
    return ids.size === 1
      ? { role_id: [...ids][0], role_type: null }
      : unscoped;
  }

  // One department, so any of its roles answers the filter the same way.
  const type = [...types][0];
  const owning =
    entries.find((entry) => entry.type === type && entry.id != null) ||
    entries.find((entry) => entry.id != null);
  return { role_id: owning?.id ?? null, role_type: type };
}

/**
 * Every card the actual view should draw, built the way the After Action
 * Report builds its rows.
 *
 * The report reads only the timecard payload, and that payload is already a
 * complete account of the day: a row for each published shift that produced a
 * timecard, a no-show row for each published shift that did not, and a row for
 * anyone who clocked in unscheduled. Each is attributed to whoever clocked it,
 * falling back to the published assignee when nobody did.
 *
 * Working from that rather than from the draft schedule means a published shift
 * shows what happened to it whatever the draft has since done: deleted,
 * reassigned, edited, or split between two people. A commitment made to an
 * employee does not stop counting because somebody edited the plan afterwards.
 */
export function buildActualCards(rows) {
  return filterCoveredNoShowRows(Array.isArray(rows) ? rows : []).map(
    (row, index) => {
      const missed = row?.result_start === "no show";
      const inProgress = row?.result_end === "in_progress";
      const coveredShifts = getCoveredPublishedShifts(row);
      const published = coveredShifts[0] || null;
      const coveredRole = fallbackRoleFromCoverage(coveredShifts);

      // A no-show row carries no worked times, so the card takes the shape of
      // the shift that was promised.
      const start = missed
        ? toHours(published?.shift_start)
        : toHours(row?.shift_start);
      // An open punch ends where the payload says it ends: the backend closes
      // it at the moment the request was served. Carrying it forward on the
      // viewer's clock was tried and taken out again, because everything it
      // needs is frozen in the same response. The break being subtracted does
      // not advance, the clock-out that ends the punch never arrives, and after
      // the business day turns over the payload's hours are on the previous
      // day's axis. Only another fetch answers any of those, and a schedule
      // that polls is a live view; the Live Action Report is the live view.
      const end = missed
        ? toHours(published?.shift_end)
        : toHours(row?.shift_end);

      const itemisedBreaks = Array.isArray(row?.unpaid_breaks)
        ? row.unpaid_breaks.filter(Boolean)
        : [];
      // A no-show has no punch, so it has no break to draw.
      const totalOnly =
        missed || itemisedBreaks.length > 0
          ? null
          : totalOnlyBreakEntry(
              toHours(row?.shift_start),
              toHours(row?.shift_end),
              toHours(row?.total_break),
              toHours(row?.break_start)
            );
      const unpaid = totalOnly ? [totalOnly] : itemisedBreaks;

      return {
        id: `actual-${published?.id ?? "none"}-${index}`,
        is_actual_card: true,
        platform_time_entry_keys: row?.platform_time_entry_keys || [],
        is_missed: missed,
        in_progress: inProgress,
        store_team_member:
          row?.store_team_member?.id ?? published?.store_team_member?.id ?? null,
        date: row?.business_date || row?.date_start || null,
        shift_start: start,
        shift_end: end,
        shift_length: Math.max(0, (end || 0) - (start || 0)),
        hours: missed ? 0 : workedHoursFromPunch(row),
        role_id: row?.actual_role_id ?? coveredRole.role_id,
        role_type: coveredRole.role_type,
        // The shape the cards already read for attendance state.
        publishedschedule: published
          ? {
              id: published.id,
              clock_in_optional: published.clock_in_optional ?? false,
              missed_shift_reason: published.missed_shift_reason ?? null,
              has_actual_used_schedule: !missed,
            }
          : null,
        // What the shift was promised as. One punch can cover more than one
        // published shift, and one published shift can produce more than one
        // punch row, so the denominator counts the shifts themselves rather
        // than the cards: every shift this card covers is listed, and the
        // consumer keys them by id so a shift split across two segments is
        // still owed once.
        covered_published: coveredShifts.map((shift) => ({
          shift_start: toHours(shift?.shift_start),
          break_start: shift?.break_start,
          break_length: shift?.break_length,
          break_start_2: shift?.break_start_2,
          break_length_2: shift?.break_length_2,
          unpaid_breaks: shift?.unpaid_breaks,
          id: shift?.id ?? null,
          hours: getPublishedShiftHours(shift),
          shift_end: toHours(shift?.shift_end),
          missed_shift_reason: shift?.missed_shift_reason ?? null,
          // Who the shift was promised to, which is not always who worked it.
          store_team_member:
            shift?.store_team_member?.id ?? shift?.store_team_member ?? null,
          // The department it was promised in, which is not always the one
          // that was clocked. The name is kept alongside the id because
          // deleting a role nulls the id and leaves the name, and the shift
          // was still worked in that department.
          role_id: shift?.role_id?.id ?? shift?.role_id ?? null,
          role_type: shift?.role_type ?? null,
        })),
        // One punch can cover more than one shift, so the pair spans the
        // coverage rather than naming the first of it. The backend sorts the
        // list, and the reports summarise it the same way.
        published_start: toHours(published?.shift_start),
        published_end: toHours(
          coveredShifts[coveredShifts.length - 1]?.shift_end
        ),
        published_shift_count: coveredShifts.length,
        unpaid_breaks: unpaid,
        paid_breaks: Array.isArray(row?.paid_breaks)
          ? row.paid_breaks.filter(Boolean)
          : [],
        break_start: unpaid[0]?.break_start ?? null,
        break_length: unpaid[0]?.break_length ?? 0,
        break_start_2: unpaid[1]?.break_start ?? null,
        break_length_2: unpaid[1]?.break_length ?? 0,
        other_store: false,
        status: "PUBLISHED",
      };
    }
  );
}

/**
 * Who has worked in the given departments, by store team member id.
 *
 * A department filter asks which work belongs to the department. A roster
 * built from current role assignments answers a different question, and an
 * employee who has since moved departments loses the hours they did work.
 * The role a punch belongs to is resolved exactly as the cards resolve it, so
 * the roster and the cards cannot disagree about which department a punch is
 * in.
 */
/**
 * Has a published shift been missed yet?
 *
 * Only once its scheduled end has passed. Comparing dates alone hides a
 * morning absence until the next business day, leaving it unclassifiable for
 * the rest of the shift it was missed on.
 */
export function missedShiftHasElapsed(date, shiftEnd, businessDate, hoursNow) {
  if (!date || !businessDate) return false;
  if (date < businessDate) return true;
  if (date > businessDate) return false;
  return (
    Number.isFinite(shiftEnd) &&
    Number.isFinite(hoursNow) &&
    shiftEnd <= hoursNow
  );
}

/**
 * Copy for the actual table when the timecard payload has no rows.
 *
 * The payload carries a no-show row for every published shift, so an empty
 * table means nothing is published for the day and nobody has clocked in.
 * Draft shifts that were never published are the one thing worth pointing at,
 * because publishing is the fix in the manager's hands; otherwise the rows
 * simply have not started arriving yet.
 */
export function describeEmptyActualTable(draftShifts) {
  // A draft at another location rides along for a shared employee
  // (other_store) and is not this store's to publish.
  const unpublished = (Array.isArray(draftShifts) ? draftShifts : []).filter(
    (shift) =>
      shift &&
      !shift.other_store &&
      !shift.publishedschedule &&
      !(Number(shift.shift_start) === 0 && Number(shift.shift_end) === 0)
  ).length;
  if (unpublished > 0) {
    const noun = unpublished === 1 ? "shift is" : "shifts are";
    return {
      title: "No shifts published for today",
      description: `${unpublished} draft ${noun} waiting to be published. Shifts appear here once the schedule is published or someone clocks in.`,
    };
  }
  return {
    title: "No shifts or clock entries yet",
    description:
      "Shifts appear here once a schedule is published for today or someone clocks in.",
  };
}
